<template>
  <div class="lead-proposals">
    <LoadingSpinner v-if="loading" />

    <EmptyState
      v-else-if="proposals.length === 0"
      :title="$t('proposals.empty_state.title')"
      :description="$t('proposals.empty_state.description')"
      icon="duotone-folders"
    >
      <SplitBtn
        v-if="formMenuItems.length > 0"
        :text="$t('proposals.create_new_proposal')"
        icon="plus"
        variant="secondary"
        :menu-items="formMenuItems"
        @click="addProposal"
        @menu-item-click="openConfigurator"
      />
      <Btn
        v-else
        :text="$t('proposals.create_new_proposal')"
        icon="plus"
        variant="secondary"
        @click="addProposal"
      />
    </EmptyState>
    <div v-else>
      <DataTable
        :title="$t('proposals.title')"
        :headers="headers"
        :items="formattedProposals"
        :loading="loading"
        v-model:selectedItems="selectedItems"
        @row-click="handleRowClick"
      >
        <template #item-title="{ item }">
          <TableRowLink
            v-if="isProposalRowLinked(item)"
            :to="proposalRoute(item)"
            :aria-label="`${$t('proposals.view')} ${item.title}`"
          >
            {{ item.title }}
          </TableRowLink>
          <span v-else>{{ item.title }}</span>
        </template>
        <template #item-status="{ status }">
          <div class="proposal-status" :class="{ ['proposal-status--' + status]: status }">
            {{ $t(`proposals.statuses.${status}`) }}
          </div>
        </template>
        <template #item-actions="{ item }">
          <div class="actions" @click.stop>
            <!-- Draft: edit actions -->
            <SplitBtn
              v-if="item.status === 'draft'"
              variant="tertiary"
              :menu-items="[
                { label: $t('proposals.edit'), value: 'edit' },
                { label: $t('proposals.duplicate'), value: 'duplicate', disabled: true },
                { label: $t('proposals.send'), value: 'send' },
                { label: $t('proposals.delete'), value: 'delete' },
              ]"
              @click="handleAction('edit', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.edit') }}
            </SplitBtn>
            <!-- Sent/Viewed: view actions with withdraw/reject option -->
            <SplitBtn
              v-else-if="item.status === 'sent' || item.status === 'viewed'"
              variant="tertiary"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.resend'), value: 'resend' },
                { label: $t('proposals.duplicate'), value: 'duplicate', disabled: true },
                { label: $t('proposals.reject'), value: 'reject' },
                { label: $t('proposals.withdraw'), value: 'withdraw' },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
            <!-- Rejected/Withdrawn: view with restore option -->
            <SplitBtn
              v-else-if="item.status === 'rejected' || item.status === 'withdrawn'"
              variant="tertiary"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.duplicate'), value: 'duplicate', disabled: true },
                { label: $t('proposals.restore'), value: 'restore' },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
            <!-- Other final statuses (accepted, superseded): view only -->
            <SplitBtn
              v-else
              variant="tertiary"
              :menu-items="[
                { label: $t('proposals.view'), value: 'view' },
                { label: $t('proposals.duplicate'), value: 'duplicate', disabled: true },
              ]"
              @click="handleAction('view', item)"
              @menu-item-click="(value) => handleAction(value, item)"
            >
              {{ $t('proposals.view') }}
            </SplitBtn>
          </div>
        </template>
        <template #table-actions>
          <Btn v-if="selectedItems.length > 0" icon="trash" @click="bulkDelete" variant="danger">
            {{ $t('proposals.delete_selected', { count: deletableSelectedCount }) }}
          </Btn>
          <SplitBtn
            v-if="formMenuItems.length > 0"
            :text="$t('proposals.add_proposal')"
            icon="plus"
            variant="secondary"
            :menu-items="formMenuItems"
            @click="addProposal"
            @menu-item-click="openConfigurator"
          />
          <Btn v-else icon="plus" @click="addProposal" variant="secondary">
            {{ $t('proposals.add_proposal') }}
          </Btn>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script>
  import { defineComponent, onMounted, onUnmounted, ref, computed, getCurrentInstance } from 'vue';
  import { crmService } from '@/services/crmService';
  import { formService } from '@/services/formService';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';

  import { formatDate } from '@/utils/date';
  import { getRejectReasonModalProps } from '@/utils/proposalRejectReason';
  import { useRouter } from 'vue-router';
  import { useOpenProposalConfigurator } from '@/composables/useOpenProposalConfigurator';

  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import Btn from '@/components/partials/Btn.vue';
  import SplitBtn from '@/components/partials/SplitBtn.vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import DataTable from '@/components/tables/DataTable.vue';
  import TableRowLink from '@/components/tables/TableRowLink.vue';
  export default defineComponent({
    name: 'LeadProposals',

    components: {
      LoadingSpinner,
      EmptyState,
      Btn,
      PageHeader,
      DataTable,
      TableRowLink,
      SplitBtn,
    },

    props: {
      leadId: {
        type: String,
        required: true,
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const loading = ref(true);
      const toastStore = useToastStore();
      const router = useRouter();
      const proposals = ref([]);
      const pagination = ref({});
      const selectedItems = ref([]);
      const configuratorForms = ref([]);
      const { openProposalConfigurator } = useOpenProposalConfigurator();

      // Compute how many selected items can be deleted (only drafts)
      const deletableSelectedCount = computed(() => {
        return selectedItems.value.filter((item) => item.status === 'draft').length;
      });

      const headers = [
        { text: $t('proposals.title_field'), value: 'title' },
        { text: $t('proposals.status'), value: 'status' },
        { text: $t('proposals.total_amount'), value: 'amount' },
        { text: $t('proposals.created_at'), value: 'createdAt' },
        { text: $t('proposals.valid_until'), value: 'validUntil' },
        { text: $t('proposals.actions'), value: 'actions' },
      ];

      const formattedProposals = computed(() => {
        return proposals.value.map((proposal) => ({
          id: proposal.id,
          createdAt: formatDate(proposal.created_at),
          title: proposal.title,
          status: proposal.status,
          amount: new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: proposal.currency,
          }).format(proposal.total_amount),
          validUntil: formatDate(proposal.valid_until),
        }));
      });

      const handleRowClick = (row) => {
        // Don't open superseded or withdrawn proposals
        if (row.status === 'superseded' || row.status === 'withdrawn') {
          return;
        }
        if (row.status === 'draft') {
          editProposal(row);
        } else {
          viewProposal(row);
        }
      };

      const isProposalRowLinked = (proposal) => {
        return proposal.status !== 'superseded' && proposal.status !== 'withdrawn';
      };

      const proposalRoute = (proposal) => ({
        name: 'ProposalDetail',
        params: { id: proposal.id },
      });

      const handleAction = (action, item) => {
        if (action === 'edit') {
          editProposal(item);
        } else if (action === 'duplicate') {
          duplicateProposal(item);
        } else if (action === 'delete') {
          deleteProposal(item);
        } else if (action === 'withdraw') {
          withdrawProposal(item);
        } else if (action === 'reject') {
          rejectProposal(item);
        } else if (action === 'restore') {
          restoreProposal(item);
        } else if (action === 'send' || action === 'view' || action === 'resend') {
          viewProposal(item);
        }
      };

      const uiStore = useUIStore();

      const addProposal = () => {
        router.push({
          name: 'ProposalCreate',
          query: { lead_id: props.leadId },
        });
      };

      const formMenuItems = computed(() => {
        return configuratorForms.value.map((form) => ({
          label: form.name,
          value: form.id,
        }));
      });

      const fetchConfiguratorForms = async () => {
        try {
          const response = await formService.getForms();
          const forms = response.forms || response || [];
          configuratorForms.value = forms.filter((form) => form.type === 'configurator');
        } catch (error) {
          console.error('Failed to fetch configurator forms:', error);
        }
      };

      const openConfigurator = (formId) => {
        const form = configuratorForms.value.find((f) => f.id === formId);
        if (!form) return;

        try {
          const returnTo =
            typeof window !== 'undefined'
              ? `${window.location.origin}/crm/lead/${props.leadId}`
              : null;
          openProposalConfigurator(
            {
              formId: form.id,
              leadId: props.leadId,
              returnTo,
            },
            $t
          );
        } catch (error) {
          console.error('Failed to open configurator:', error);
          toastStore.addToast({
            message: $t('proposals.error_opening_configurator'),
            type: 'error',
            duration: 5000,
          });
        }
      };

      const fetchProposals = async () => {
        try {
          const response = await crmService.getProposals(props.leadId);
          proposals.value = response.proposals;
          pagination.value = response.pagination;
        } catch (error) {
          console.error('Failed to fetch proposals:', error);
        }
      };

      const handleWindowFocus = () => {
        fetchProposals();
      };

      const editProposal = (proposal) => {
        router.push({
          name: 'ProposalDetail',
          params: { id: proposal.id },
        });
      };

      const viewProposal = (proposal) => {
        router.push({
          name: 'ProposalDetail',
          params: { id: proposal.id },
        });
      };

      const deleteProposal = async (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.delete_proposal'),
          description: $t('proposals.delete_proposal_confirmation'),
          status: 'danger',
          onSubmit: async () => {
            try {
              await crmService.deleteProposal(props.leadId, proposal.id);
              proposals.value = proposals.value.filter((p) => p.id !== proposal.id);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_deleted'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error deleting proposal:', error);
              const errorMessage = error.response?.data?.error || $t('proposals.error_deleting');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const withdrawProposal = async (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.withdraw_proposal'),
          description: $t('proposals.withdraw_proposal_confirmation'),
          status: 'warning',
          showReasonInput: true,
          reasonLabel: $t('proposals.withdraw_reason_label'),
          reasonPlaceholder: $t('proposals.withdraw_reason_placeholder'),
          onSubmit: async (reason) => {
            try {
              const updatedProposal = await crmService.withdrawProposal(
                props.leadId,
                proposal.id,
                reason || null
              );
              // Update proposal in state
              const index = proposals.value.findIndex((p) => p.id === proposal.id);
              if (index !== -1) {
                proposals.value[index] = updatedProposal;
              }
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_withdrawn'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error withdrawing proposal:', error);
              const errorMessage = error.response?.data?.error || $t('proposals.error_withdrawing');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const rejectProposal = async (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.reject_proposal'),
          description: $t('proposals.reject_proposal_confirmation'),
          status: 'warning',
          ...getRejectReasonModalProps($t),
          onSubmit: async (reason, { reasonCode } = {}) => {
            try {
              const updatedProposal = await crmService.rejectProposal(
                props.leadId,
                proposal.id,
                reason || null,
                reasonCode || null
              );
              // Update proposal in state
              const index = proposals.value.findIndex((p) => p.id === proposal.id);
              if (index !== -1) {
                proposals.value[index] = updatedProposal;
              }
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_rejected'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error rejecting proposal:', error);
              const errorMessage = error.response?.data?.error || $t('proposals.error_rejecting');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const restoreProposal = async (proposal) => {
        const modalId = uiStore.addModal(null, {
          title: $t('proposals.restore_proposal'),
          description: $t('proposals.restore_proposal_confirmation'),
          status: 'info',
          onSubmit: async () => {
            try {
              const updatedProposal = await crmService.restoreProposal(props.leadId, proposal.id);
              // Update proposal in state
              const index = proposals.value.findIndex((p) => p.id === proposal.id);
              if (index !== -1) {
                proposals.value[index] = updatedProposal;
              }
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.proposal_restored'),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error restoring proposal:', error);
              const errorMessage = error.response?.data?.error || $t('proposals.error_restoring');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      const bulkDelete = async () => {
        // Filter only draft proposals (only drafts can be deleted)
        const deletableItems = selectedItems.value.filter((item) => item.status === 'draft');

        if (deletableItems.length === 0) {
          toastStore.addToast({
            message: $t('proposals.bulk_delete_no_drafts'),
            type: 'warning',
            duration: 3000,
          });
          return;
        }

        const nonDeletableCount = selectedItems.value.length - deletableItems.length;
        let description = $t('proposals.bulk_delete_confirmation', {
          count: deletableItems.length,
        });
        if (nonDeletableCount > 0) {
          description += ' ' + $t('proposals.bulk_delete_skipped', { count: nonDeletableCount });
        }

        const modalId = uiStore.addModal(null, {
          title: $t('proposals.bulk_delete_title'),
          description: description,
          status: 'danger',
          onSubmit: async () => {
            try {
              const proposalIds = deletableItems.map((item) => item.id);
              await crmService.bulkDeleteProposals(props.leadId, proposalIds);

              // Remove deleted proposals from local state
              const deletedIds = new Set(proposalIds);
              proposals.value = proposals.value.filter((p) => !deletedIds.has(p.id));

              // Clear selection
              selectedItems.value = [];

              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('proposals.bulk_delete_success', { count: deletableItems.length }),
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error bulk deleting proposals:', error);
              const errorMessage =
                error.response?.data?.error || $t('proposals.error_bulk_deleting');
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
              uiStore.closeModal(modalId);
            }
          },
        });
      };

      onMounted(async () => {
        await Promise.all([fetchProposals(), fetchConfiguratorForms()]);
        loading.value = false;
        window.addEventListener('focus', handleWindowFocus);
      });

      onUnmounted(() => {
        window.removeEventListener('focus', handleWindowFocus);
      });

      return {
        formatDate,
        loading,
        proposals,
        pagination,
        selectedItems,
        deletableSelectedCount,
        addProposal,
        formMenuItems,
        openConfigurator,
        headers,
        formattedProposals,
        handleRowClick,
        isProposalRowLinked,
        proposalRoute,
        handleAction,
        bulkDelete,
      };
    },
  });
</script>

<style scoped lang="scss">
  .lead-proposals {
    .proposal-status {
      display: inline-block;
      padding: 4px 8px;
      border-radius: 0.5rem;
      background-color: var(--slate-200);
      color: var(--slate-900);
      border: 1px solid var(--slate-300);
      &--sent {
        background-color: var(--amber-100);
        color: var(--amber-600);
        border: 1px solid var(--amber-500);
      }
      &--viewed {
        background-color: var(--brand-100);
        color: var(--brand-600);
        border: 1px solid var(--brand-300);
      }
      &--accepted {
        background-color: var(--emerald-100);
        color: var(--emerald-600);
        border: 1px solid var(--emerald-300);
      }
      &--rejected {
        background-color: var(--red-100);
        color: var(--red-600);
        border: 1px solid var(--red-300);
      }
      &--superseded {
        background-color: var(--red-100);
        color: var(--red-600);
        border: 1px solid var(--red-500);
      }
      &--withdrawn {
        background-color: var(--orange-100);
        color: var(--orange-600);
        border: 1px solid var(--orange-300);
      }
    }
  }
</style>
